import React, { useContext, useEffect, useState, useRef } from "react";
import OfferCard from "../OfferCard/OfferCard";
import { OffersContext } from "../../../context/OffersContext";
import { AuthContext } from "../../../context/AuthContext";
import { fetchPackagesBatch } from "../../../services/PackagesService";
import RegionCountriesModal from "../RegionCountriesModal/RegionCountriesModal";
import LinkButton from "../../Elements/LinkButton/LinkButton";
import EsimCheckModal from "../EsimCheckModal/EsimCheckModal";
import AddedToCartModal from "../AddedToCartModal/AddedToCartModal";
import MainButton from "../../Elements/MainButton/MainButton";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import { useCart } from "../../../context/CartContext";
import { useNavigate } from "react-router-dom";
import QuantityPicker from "../QuantityPicker/QuantityPicker";
import phoneIcon from "../../../assets/package/phone.png";

import "./OffersList.css";

const OffersList = () => {
  // Global context
  const { selectedLocationId, selectedLocationType, selectedLocationName } = useContext(OffersContext);
  const { addItems, items: cartItems } = useCart();
  const { walletCurrency } = useContext(AuthContext);

  const navigate = useNavigate();
  const listRef = useRef(null);

  // Local state: packages and selection
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(1);

  // Modal state
  const [isEsimModalOpen, setIsEsimModalOpen] = useState(false);
  const [isAddedModalOpen, setIsAddedModalOpen] = useState(false);
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);

  // Computed: currently selected package
  const selectedPackage = packages.find((p) => p.id === selectedPackageId) || null;

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity > 1) {
      alert("Multi-quantity purchases coming soon!\nFor now, only 1 eSIM at a time.");
      return;
    }
    setQuantity(newQuantity);
  };

  // Add selected packages to cart (multiple quantity)
  const handleAddToCart = () => {
    if (cartItems.length > 0) {
      alert("Multi-item purchases coming soon!\nClear cart first or buy current items.");
      return;
    }
    if (!selectedPackage || quantity < 1) return;

    const itemsToAdd = Array.from({ length: quantity }, () => ({
      id: selectedPackage.id,
      name: selectedPackage.name,
      price: selectedPackage.price,
      currency: selectedPackage.currency,
      gigabytes: selectedPackage.gigabytes,
      duration: selectedPackage.duration,
      top_up_available: selectedPackage.top_up_available,
      country_id: selectedPackage.country_id,
      region_id: selectedPackage.region_id,
    }));

    addItems(itemsToAdd);
    setIsAddedModalOpen(true);
  };

  // Add to cart + redirect to checkout
  const handleBuyNow = () => {
    handleAddToCart();
    navigate("/cart");
  };

  // Load packages for selected country/region
  useEffect(() => {
    setSelectedPackageId(null);
    setQuantity(1);

    if (!selectedLocationId) {
      setPackages([]);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const isCountry = selectedLocationType === "country";

        const data = await fetchPackagesBatch({
          sortBy: "gigabytes",
          ascOrder: true,
          limit: 50,
          skip: 0,
          countryId: isCountry ? selectedLocationId : null,
          regionId: !isCountry ? selectedLocationId : null,
          currency: walletCurrency || "USD",
        });

        const items = data.items.filter((pkg) => pkg.active === true) || [];
        setPackages(items);
      } catch (e) {
        setError("Failed to load packages");
        setPackages([]);
        setSelectedPackageId(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedLocationId, selectedLocationType, walletCurrency]);

  // Toggle package selection (single select)
  const togglePackage = (id) => {
    setSelectedPackageId((prev) => (prev === id ? null : id));
  };

  // Dynamic title based on selected location
  const titleText = selectedLocationName
    ? `Pick the Best eSIM Plan for ${selectedLocationName} Travel`
    : "Pick the Best eSIM Plan";

  const isRegion = selectedLocationType === "region";

  return (
    <div className="offers-list" ref={listRef}>
      {/* Header: title + region link + eSIM check button */}
      <div className="offers-list-header">
        <div className="offers-list-header-left">
          <h2 className="offers-title">{titleText}</h2>

          {isRegion && selectedLocationId && (
            <div className="offers-region-link">
              <LinkButton onClick={() => setIsRegionModalOpen(true)}>View countries in this region</LinkButton>
            </div>
          )}
        </div>

        <button type="button" className="offers-esim-check-btn" onClick={() => setIsEsimModalOpen(true)}>
          <img src={phoneIcon} alt="" className="offers-esim-check-icon" />
          <span>Check eSIM device</span>
        </button>
      </div>

      {/* Loading / Error states */}
      {loading && <p className="text-muted">Loading packages...</p>}
      {error && <p className="text-muted">{error}</p>}
      {!loading && !error && !selectedLocationId && (
        <p className="text-muted">Select a country or region to see plans.</p>
      )}

      {/* Grid of OfferCards */}
      {!loading && !error && selectedLocationId && (
        <div className="offers-grid-wrapper">
          <div className="offers-grid">
            {packages.map((pkg) => (
              <OfferCard
                key={pkg.id}
                id={pkg.id}
                gigabytes={pkg.gigabytes}
                duration={pkg.duration}
                price={pkg.price}
                currency={pkg.currency}
                selected={selectedPackageId === pkg.id}
                onToggle={() => togglePackage(pkg.id)}
                topUpAvailable={pkg.top_up_available}
              />
            ))}
          </div>
        </div>
      )}

      {/* Action bar: quantity + buttons (when packages available) */}
      {!loading && !error && selectedLocationId && packages.length > 0 && (
        <div className="offers-actions-bar">
          <QuantityPicker value={quantity} onChange={handleQuantityChange} />

          <SecondButton onClick={handleAddToCart} disabled={!selectedPackage}>
            Add to cart
          </SecondButton>

          <MainButton onClick={handleBuyNow} disabled={!selectedPackage}>
            Buy now
          </MainButton>
        </div>
      )}

      {/* Modals */}
      <EsimCheckModal isOpen={isEsimModalOpen} onClose={() => setIsEsimModalOpen(false)} />
      <RegionCountriesModal
        isOpen={isRegionModalOpen}
        onClose={() => setIsRegionModalOpen(false)}
        regionId={selectedLocationId}
        regionName={selectedLocationName}
      />
      <AddedToCartModal
        isOpen={isAddedModalOpen}
        onClose={() => setIsAddedModalOpen(false)}
        onGoToCart={() => {
          setIsAddedModalOpen(false);
          navigate("/cart");
        }}
        item={selectedPackage}
        quantity={quantity}
      />
    </div>
  );
};

export default OffersList;
